import { Box, Skeleton, styled, Typography } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useChannelStats } from "@/hooks/use-channel-stats";
import TransactionTrend from "./_components/transaction-trend";
import { BaseConfig } from "./_components/base-config";
import { baseConfigSchema } from "@/schemas/product.schema";
import { useFormik } from "formik";

export function fmtVolume(n) {
  if (n >= 1e12) return `₦${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `₦${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `₦${(n / 1e6).toFixed(1)}M`;
  return `₦${Math.round(n).toLocaleString()}`;
}

export function srColor(rate) {
  if (rate === null) return "text.disabled";
  if (rate >= 96) return "#15803d";
  if (rate >= 93) return "#d97706";
  return "#dc2626";
}

const Root = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
}));

const KpiGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1.5),
  gridTemplateColumns: "repeat(2, 1fr)",
  [theme.breakpoints.up("sm")]: { gridTemplateColumns: "repeat(4, 1fr)" },
}));

const KpiBox = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.04)"
      : theme.palette.grey[50],
  borderRadius: 8,
  padding: theme.spacing(1.5),
}));

const Overview = () => {
  const { productId } = useParams();
  const [period, setPeriod] = useState("30d");

  const { data, isLoading } = useChannelStats(period);
  const channelStats = data?.channels?.find((c) => c.channel === productId);
  const product = channelStats && {
    stats: {
      volume: channelStats.totalVolume,
      count: channelStats.totalCount,
      success_rate:
        channelStats.totalCount > 0 ? channelStats.successRate : null,
      avg_value: channelStats.averageValue,
    },
  };

  const configFormik = useFormik({
    enableReinitialize: true,
    initialValues: product?.config || {
      visibility: true,
      minAmount: "",
      maxAmount: "",
      dailyLimit: "",
    },
    validationSchema: () => baseConfigSchema,
    onSubmit: (values) => {
      console.log(values);
    },
  });

  if (isLoading)
    return (
      <Root>
        <Card>
          <KpiGrid>
            {Array.from({ length: 4 }).map((_, i) => (
              <KpiBox key={i}>
                <Skeleton height={44} />
              </KpiBox>
            ))}
          </KpiGrid>
        </Card>
        <Card>
          <Skeleton
            variant="rectangular"
            height={320}
            sx={{ borderRadius: 2 }}
          />
        </Card>
      </Root>
    );

  if (!product)
    return (
      <Root>
        <Card>
          <Typography color="text.secondary">Product not found.</Typography>
        </Card>
      </Root>
    );

  const stats = product.stats;

  return (
    <Root>
      <Card>
        <KpiGrid>
          <KpiBox>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Volume
            </Typography>
            <Typography fontWeight={600} fontSize="1rem" sx={{ mt: 0.25 }}>
              {stats.volume === 0 ? "—" : fmtVolume(stats.volume)}
            </Typography>
          </KpiBox>

          <KpiBox>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Transactions
            </Typography>
            <Typography fontWeight={600} fontSize="1rem" sx={{ mt: 0.25 }}>
              {stats.count === 0 ? "—" : stats.count.toLocaleString()}
            </Typography>
          </KpiBox>

          <KpiBox>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Success rate
            </Typography>
            <Typography
              fontWeight={600}
              fontSize="1rem"
              sx={{ mt: 0.25, color: srColor(stats.success_rate) }}
            >
              {stats.success_rate !== null ? `${stats.success_rate}%` : "N/A"}
            </Typography>
          </KpiBox>

          <KpiBox>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Avg value
            </Typography>
            <Typography fontWeight={600} fontSize="1rem" sx={{ mt: 0.25 }}>
              {stats.avg_value === 0 ? "—" : fmtVolume(stats.avg_value)}
            </Typography>
          </KpiBox>
        </KpiGrid>
      </Card>

      <TransactionTrend
        productId={productId}
        period={period}
        onPeriodChange={setPeriod}
      />

      <Card>
        <form onSubmit={configFormik.handleSubmit}>
          <BaseConfig formik={configFormik} isDisabled={false} />
        </form>
      </Card>
    </Root>
  );
};

export default Overview;
